import { useRef, useCallback } from 'react';
import { ModifyObjectCommand } from '../commands/ModifyObjectCommand';
import {
  snapValue,
  applyHierarchicalSnap,
  ENDPOINT_SNAP_DISTANCE,
} from '../components/Editor/tools/snapUtils';
import { createVertexHandle, isVertexHandle } from '../components/Editor/tools/vertexEditUtils';
import {
  extractAnchorPoints,
  pathLocalToWorld,
  updateAnchorPoint,
  removeAnchorPoint,
  positionHandlesFromPath,
  recomputePathBBoxKeepingPosition,
} from '../components/Editor/tools/bezierVertexEditUtils';

/**
 * useBezierVertexEditing - React hook for bezier curve anchor-point edit mode
 *
 * Mirrors useVertexEditing for polygons but adapted for fabric.Path objects:
 * - Double-click enters edit mode → draggable handles at each anchor point
 * - Snap-to-grid (via snapValue, NOT applyHierarchicalSnap — see plan for why)
 * - Undo/redo via ModifyObjectCommand (stores full path.path array)
 * - Syncs handle count when anchors are added/removed externally (properties panel)
 * - Auto-exits on tool change or click-outside (managed by GardenCanvas)
 *
 * @param {React.MutableRefObject<fabric.Canvas>} canvasRef
 * @param {object} commandStack - undo/redo command stack
 * @param {React.MutableRefObject<{snapEnabled: boolean, gridSize: number}>} snapSettingsRef
 * @returns {{ editingPathRef, enterEditMode, exitEditMode, refreshHandles, isInEditMode, isVertexHandle }}
 */
export function useBezierVertexEditing(canvasRef, commandStack, snapSettingsRef) {
  const editingPathRef = useRef(null);
  const handlesRef = useRef([]);
  // Full path.path array snapshot at drag-start for undo command
  const oldPathDataRef = useRef(null);
  // Saved original stroke for restoration on exit
  const originalStrokeRef = useRef(null);
  // Ctrl key state for snap override
  const ctrlPressedRef = useRef(false);
  const ctrlCleanupRef = useRef(null);
  // Listener for path 'modified' event (undo/redo, properties panel changes)
  const pathModifiedListenerRef = useRef(null);

  /**
   * Reposition all handles to match current anchor world positions.
   * Called externally if anchor positions change without triggering 'modified'.
   */
  const refreshHandles = useCallback(() => {
    const path = editingPathRef.current;
    const canvas = canvasRef.current;
    if (!path || !canvas || handlesRef.current.length === 0) return;
    positionHandlesFromPath(path, handlesRef.current);
    canvas.requestRenderAll();
  }, [canvasRef]);

  /**
   * Exit vertex edit mode: remove handles, restore path appearance.
   */
  const exitEditMode = useCallback(() => {
    const path = editingPathRef.current;
    const canvas = canvasRef.current;
    if (!path || !canvas) return;

    // Restore original stroke style
    path.set({
      strokeDashArray: originalStrokeRef.current?.strokeDashArray ?? null,
      lockMovementX: false,
      lockMovementY: false,
      lockRotation: false,
      lockScalingX: false,
      lockScalingY: false,
    });

    // Remove all handles from canvas (batch)
    const handlesToRemove = [...handlesRef.current];
    if (handlesToRemove.length > 0) {
      canvas.remove(...handlesToRemove);
    }
    handlesRef.current = [];

    // Remove path 'modified' listener
    if (pathModifiedListenerRef.current) {
      path.off('modified', pathModifiedListenerRef.current);
      pathModifiedListenerRef.current = null;
    }

    // Remove Ctrl key tracking
    if (ctrlCleanupRef.current) {
      ctrlCleanupRef.current();
      ctrlCleanupRef.current = null;
    }

    editingPathRef.current = null;
    oldPathDataRef.current = null;
    originalStrokeRef.current = null;

    path.setCoords();
    canvas.requestRenderAll();
  }, [canvasRef]);

  /**
   * Enter vertex edit mode for a bezier path.
   * Creates draggable handles at each anchor point.
   */
  const enterEditMode = useCallback((path) => {
    const canvas = canvasRef.current;
    if (!canvas || !path) return;

    // Already editing this path
    if (editingPathRef.current === path) return;

    // Exit any existing edit session first
    if (editingPathRef.current) exitEditMode();

    editingPathRef.current = path;

    originalStrokeRef.current = {
      strokeDashArray: path.strokeDashArray ?? null,
    };

    // Visual feedback: dashed stroke while editing. NOT for a centerline weg (#720):
    // its stroke is a wide, pattern-filled band — dashing it chops the band into
    // short pattern-filled chunks with gaps (looks like the texture shattered). The
    // green handles + locked state are cue enough; leave the ribbon solid.
    const isCenterlineRibbon = path.data?.shapeType === 'centerline';
    path.set({
      ...(isCenterlineRibbon ? {} : { strokeDashArray: [8, 4] }),
      lockMovementX: true,
      lockMovementY: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
    });

    // Snapshot full path array for undo command on first drag
    oldPathDataRef.current = path.path.map(cmd => [...cmd]);

    // Track Ctrl key for snap override
    const handleCtrlDown = (e) => { if (e.key === 'Control') ctrlPressedRef.current = true; };
    const handleCtrlUp = (e) => { if (e.key === 'Control') ctrlPressedRef.current = false; };
    window.addEventListener('keydown', handleCtrlDown);
    window.addEventListener('keyup', handleCtrlUp);
    ctrlCleanupRef.current = () => {
      window.removeEventListener('keydown', handleCtrlDown);
      window.removeEventListener('keyup', handleCtrlUp);
    };

    /**
     * Create one handle for anchor at `index` and attach event listeners.
     * Extracted as a named function so handlePathModified can also call it
     * when anchors are added externally.
     */
    const makeHandle = (index) => {
      const anchors = extractAnchorPoints(path.path);
      const worldPoint = pathLocalToWorld(path, anchors[index]);
      const handle = createVertexHandle(worldPoint.x, worldPoint.y, index);

      handle.on('moving', () => {
        const { snapEnabled, gridSize } = snapSettingsRef?.current ?? {};

        // #720: the two END handles of a centerline weg snap to other objects'
        // endpoints (terrace/gate corners), so a path can be anchored to them.
        // The exclusion predicate drops the weg itself and the transient vertex
        // handles — without it snapToNearestEndpoint locks onto the dragged
        // handle's own center (distance 0) and no snap (grid OR endpoint) ever wins.
        const isCenterline = path.data?.shapeType === 'centerline';
        const anchorCount = isCenterline ? extractAnchorPoints(path.path).length : 0;
        const isEndpointHandle = isCenterline && (index === 0 || index === anchorCount - 1);

        if (isEndpointHandle) {
          const snap = applyHierarchicalSnap(
            { x: handle.left, y: handle.top },
            canvas,
            gridSize ?? 10,
            snapEnabled ?? true,
            ctrlPressedRef.current,
            ENDPOINT_SNAP_DISTANCE,
            (obj) => obj === path || isVertexHandle(obj),
          );
          handle.set({ left: snap.point.x, top: snap.point.y });
        } else {
          // Interior handles: grid snap only. applyHierarchicalSnap MUST NOT be used
          // here — snapToNearestEndpoint would find the handle's own center (distance 0)
          // and "snap" to the raw position, preventing grid snap from ever triggering.
          handle.set({
            left: snapValue(handle.left, gridSize ?? 10, snapEnabled ?? true, ctrlPressedRef.current),
            top: snapValue(handle.top, gridSize ?? 10, snapEnabled ?? true, ctrlPressedRef.current),
          });
        }
        handle.setCoords();

        // Move the anchor point: converts world → local, regenerates Catmull-Rom, updates bbox
        updateAnchorPoint(path, index, { x: handle.left, y: handle.top });
        canvas.requestRenderAll();
      });

      // #720: Alt-click a handle to delete that centerline point (min 2 remain).
      // Fires on mousedown before any drag begins; a plain (non-Alt) click still
      // starts a normal move. removeAnchorPoint keeps the width untouched and
      // rewrites data.centerline; the ModifyObjectCommand makes it undoable and its
      // 'modified' fire triggers handlePathModified (handle-count + centerline resync).
      handle.on('mousedown', (opt) => {
        if (!opt.e?.altKey || path.data?.shapeType !== 'centerline') return;
        const before = path.path.map(cmd => [...cmd]);
        removeAnchorPoint(path, index);
        const after = path.path.map(cmd => [...cmd]);
        if (before.length === after.length) return; // at minimum → no-op
        // The clicked handle may be the one handlePathModified removes; drop Fabric's
        // active object so no stale transform target dangles past this mouse gesture.
        canvas.discardActiveObject();
        if (commandStack) {
          commandStack.execute(new ModifyObjectCommand(path, { path: before }, { path: after }));
        } else {
          path.fire('modified');
        }
      });

      handle.on('modified', () => {
        // Commit undo command after drag ends (stores full path.path array)
        if (oldPathDataRef.current && commandStack) {
          const currentPathData = path.path.map(cmd => [...cmd]);
          const command = new ModifyObjectCommand(
            path,
            { path: oldPathDataRef.current },
            { path: currentPathData }
          );
          commandStack.execute(command);
          // Update snapshot for next drag
          oldPathDataRef.current = currentPathData;
        }

        // Notify properties panel to refresh anchor-point display
        path.fire('modified');
      });

      return handle;
    };

    // Create all handles (batch add to avoid per-add re-renders)
    canvas.renderOnAddRemove = false;
    const anchors = extractAnchorPoints(path.path);
    const handles = anchors.map((_, index) => makeHandle(index));
    handles.forEach(h => canvas.add(h));
    canvas.renderOnAddRemove = true;
    handlesRef.current = handles;

    /**
     * Called when path data changes externally (undo/redo via ModifyObjectCommand,
     * or add/remove via properties panel which calls path.fire('modified')).
     *
     * Responsibilities:
     * 1. Recompute bbox (width/height/pathOffset/left/top) from new path.path
     * 2. Sync handle count (add/remove handles to match new anchor count)
     * 3. Reposition all handles
     * 4. Update undo snapshot
     */
    const handlePathModified = () => {
      const anchors = extractAnchorPoints(path.path);

      // Recompute bbox + reposition, rotation/scale-safe (same helper applyNewAnchors
      // uses, so live drags and undo/redo/panel edits stay consistent — #720).
      recomputePathBBoxKeepingPosition(path);

      // #720: undo/redo restores path.path directly (ModifyObjectCommand), bypassing
      // applyNewAnchors — re-derive data.centerline from the restored anchors so it
      // never drifts from the geometry (only applyWegGeometry reads it, but a stale
      // value would silently revert the edit on the next width/verlauf change).
      if (path.data?.shapeType === 'centerline') {
        path.data.centerline = anchors.map(p => ({ x: p.x, y: p.y }));
      }

      // Sync handle count: add missing or remove excess handles
      const currentCount = handlesRef.current.length;
      const newCount = anchors.length;

      if (newCount > currentCount) {
        canvas.renderOnAddRemove = false;
        for (let i = currentCount; i < newCount; i++) {
          const h = makeHandle(i);
          canvas.add(h);
          handlesRef.current.push(h);
        }
        canvas.renderOnAddRemove = true;
      } else if (newCount < currentCount) {
        const removed = handlesRef.current.splice(newCount);
        canvas.remove(...removed);
      }

      // Update snapshot so next drag starts from the restored state
      oldPathDataRef.current = path.path.map(cmd => [...cmd]);

      positionHandlesFromPath(path, handlesRef.current);
      path.setCoords();
      canvas.requestRenderAll();
    };

    path.on('modified', handlePathModified);
    pathModifiedListenerRef.current = handlePathModified;

    path.setCoords();
    canvas.requestRenderAll();
  }, [canvasRef, commandStack, exitEditMode, snapSettingsRef]);

  return {
    editingPathRef,
    enterEditMode,
    exitEditMode,
    refreshHandles,
    /** True when a bezier path is currently in vertex edit mode */
    isInEditMode: () => editingPathRef.current !== null,
    isVertexHandle,
  };
}
